<template>
  <div>
    <n-search-container
      :fields="searchFields"
      @search="search"
      @reset="reset"
    >
      <n-search-input
        ref="search"
        v-model="params"
        :fields="searchFields"
      />
    </n-search-container>

    <b-card
      no-body
      class="mb-0 py-2"
    >
      <b-card-text class="mb-2 d-flex justify-content-between">
        <b-col>
          <label>{{ $t('general.entries') }}</label>
          <v-select
            v-model="perPage"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block ml-50 mr-1"
          >
            <template #no-options>
              {{ $t("selection.noOption") }}
            </template>
          </v-select>
        </b-col>
        <b-button
          v-if="!$can('manage', manageRemotePermission) && $can('manage', 'manage-service-category')"
          class="mr-1"
          :style="{ marginTop: '10px' }"
          variant="primary"
          size="sm"
          @click="openModal()"
        >
          {{ $t('button.add') }}
        </b-button>
      </b-card-text>

      <!-- Categories Table -->
      <div>
        <n-table
          ref="table"
          :fields="fields"
          :items="categories"
          :busy="loading"
          :current-page="params.page"
          :per-page="$store.state.pagination.perPage"
          :total="total"
          :deletable="true"
          :updatable="true"
          :type="permissionType"
          :route="route"
          @sort-changed="sortChanged"
          @destroy="handleDelete"
        >
          <template #cell(name)="data">
            {{ getNameByLang(data.item) }}
          </template>
          <template #cell(code)="data">
            {{ data.item.code || '-' }}
          </template>
          <template #cell(description)="data">
            {{ data.item.description || '-' }}
          </template>
          <template #cell(isActive)="data">
            <b-badge
              v-if="data.item.isActive"
              pill
              variant="success"
              badge-glow
            >
              {{ $t(`status.active`) }}
            </b-badge>
            <b-badge
              v-else
              pill
              variant="danger"
              badge-glow
            >
              {{ $t(`status.inactive`) }}
            </b-badge>
          </template>
          <template #cell(actions)="data">
            <div class="text-nowrap">
              <template v-if="!$can('manage', manageRemotePermission) && $can('manage', 'manage-service-category')">
                <b-button
                  v-if="$can('update', permissionType)"
                  v-b-tooltip.hover
                  :title="$t('general.update')"
                  variant="primary"
                  pill
                  size="sm"
                  @click="openModal(data.item)"
                >
                  <feather-icon icon="EditIcon" />
                </b-button>
                <b-button
                  v-if="$can('delete', permissionType)"
                  v-b-tooltip.hover
                  :title="$t('general.delete')"
                  variant="danger"
                  pill
                  size="sm"
                  @click="confirmDelete(data.item.id)"
                >
                  <feather-icon icon="TrashIcon" />
                </b-button>
              </template>
              <b-button
                v-else
                v-b-tooltip.hover
                :title="$t('general.not_allow_to_modify')"
                variant="secondary"
                pill
                size="sm"
              >
                <feather-icon icon="LockIcon" />
              </b-button>
            </div>
          </template>
        </n-table>

        <!-- Pagination -->
        <n-pagination
          class="mt-2"
          :total="total"
          :per-page="$store.state.pagination.perPage"
          :page="params.page"
          @change="onPageChange"
        />
      </div>

      <!-- Category Modal -->
      <b-modal
        v-model="showModal"
        :title="modalMode === 'add' ? $t('button.addServiceCategory') : $t('button.editServiceCategory')"
        size="lg"
        hide-footer
        @hidden="resetForm"
      >
        <b-form>
          <validation-observer
            ref="createForm"
            #default="{ invalid }"
          >
            <!-- Name Field with Language Support -->
            <validation-provider
              v-slot="{ errors }"
              :name="$t('field.name')"
              :rules="locale === $i18n.locale.split('-')[0] ? 'required|min:3|max:47' : 'min:3|max:47'"
            >
              <b-form-group :label="$t('field.name')">
                <b-row>
                  <b-col>
                    <b-form-input
                      v-model="form.name[locale]"
                      :placeholder="$t('placeholder.enterTitle')"
                      :state="errors.length > 0 ? false : null"
                    />
                  </b-col>
                  <b-col
                    cols="1"
                    class="d-flex align-items-center justify-content-end"
                  >
                    <b-form-select
                      v-model="locale"
                      :options="locales"
                      :style="{
                        minWidth: '65px',
                        width: 'auto',
                        maxWidth: '120px'
                      }"
                    />
                  </b-col>
                </b-row>
                <small
                  class="text-danger"
                  :style="{ paddingTop: '5px' }"
                >
                  {{ formatErrorMessage(errors[0]) }}
                </small>
              </b-form-group>
            </validation-provider>

            <!-- Category Code Field -->
            <validation-provider
              v-slot="{ errors }"
              :name="$t('field.code')"
              rules="required|min:2|max:20"
            >
              <b-form-group :label="$t('field.code')">
                <!-- <b-form-input
                  v-model="form.code"
                  :disabled="modalMode === 'edit'"
                  :placeholder="$t('placeholder.enterCode')"
                  :state="errors.length > 0 ? false : null"
                /> -->
                <!-- single select -->
                <n-single-select
                  v-model="form.code"
                  :options="[
                    {
                      code: 'MAINTENANCE',
                      text: 'MAINTENANCE',
                    },
                    {
                      code: 'SERVICE_ORDER',
                      text: 'SERVICE_ORDER',
                    }
                  ]"
                  :readonly="modalMode === 'edit'"
                  selection-key="code"
                  selection-label="text"
                  :reduce="true"
                  :placeholder="$t('placeholder.select_code')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <b-form-group
              :label="$t('field.description')"
              label-for="description"
            >
              <b-form-textarea
                id="description"
                v-model="form.description"
                :placeholder="$t('placeholder.enterDescription')"
                rows="3"
              />
            </b-form-group>

            <validation-provider
              v-if="modalMode === 'edit'"
              v-slot="{ errors }"
              :name="$t('field.status')"
              rules="required"
            >
              <b-form-group
                :label="$t('field.status')"
                label-for="isActive"
              >
                <b-form-radio-group
                  id="isActive"
                  v-model="form.isActive"
                  :options="[
                    { text: $t('status.active'), value: true },
                    { text: $t('status.inactive'), value: false }
                  ]"
                  buttons
                  button-variant="outline-primary rounded-0"
                  class="flex-wrap"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
            <div class="d-flex justify-content-end">
              <n-button-loading
                variant="primary"
                :loading="submitLoading"
                :disabled="!form.name[locale] || !form.code || invalid"
                @submit="handleSubmit"
              >
                {{ $t('button.save') }}
              </n-button-loading>
            </div>
          </validation-observer>
        </b-form>
      </b-modal>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BCardText,
  BCol,
  BFormTextarea,
  BFormSelect,
  BRow,
  BFormRadioGroup,
  BBadge,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import NTable from '@/components/NTable.vue'
import NPagination from '@/components/NPagination.vue'
import NSearchContainer from '@/components/NSearchContainer.vue'
import NSearchInput from '@/components/NSearchInput.vue'
import Repository from '@/repositories/RepositoryFactory'
import NButtonLoading from '@/components/NButtonLoading.vue'
import NSingleSelect from '@/components/NSingleSelect.vue'

const ServiceSystemRepository = Repository.get('serviceSystem')

export default {
  name: 'ServiceCategory',
  components: {
    BCard,
    BButton,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BCardText,
    BCol,
    BRow,
    vSelect,
    NTable,
    NPagination,
    NSearchContainer,
    NSearchInput,
    BFormTextarea,
    BFormSelect,
    ValidationProvider,
    ValidationObserver,
    BFormRadioGroup,
    BBadge,
    NButtonLoading,
    NSingleSelect,
  },
  data() {
    return {
      categories: [],
      fields: [
        {
          key: 'name',
          label: 'field.name',
          sortable: true,
        },
        {
          key: 'code',
          label: 'field.code',
          sortable: true,
        },
        {
          key: 'description',
          label: 'field.description',
          sortable: false,
        },
        {
          key: 'isActive',
          label: 'field.status',
          sortable: false,
        },
        {
          key: 'actions',
          label: 'general.action',
        },
      ],
      searchFields: [
        {
          key: 'name',
          label: 'field.name',
          type: 'text',
          md: 6,
          lg: 6,
        },
        // {
        //   key: 'code',
        //   label: 'field.code',
        //   type: 'text',
        //   md: 4,
        //   lg: 4,
        // },
      ],
      showModal: false,
      modalMode: 'add',
      form: {
        name: {
          en: '',
          zh: '',
          km: '',
          vi: '',
        },
        code: '',
        description: '',
        isActive: true,
      },
      selectedCategory: null,
      loading: false,
      perPage: this.$store.state.pagination.perPage,
      perPageOptions: this.$store.state.pagination.perPageOptions,
      total: 0,
      params: {
        page: 1,
        name: null,
        order: null,
        sort: null,
        code: null,
      },
      locale: this.$i18n.locale.split('-')[0],
      locales: [
        { value: 'en', text: 'English' },
        { value: 'zh', text: '中文' },
        { value: 'km', text: 'ខ្មែរ' },
        { value: 'vi', text: 'Tiếng Anh' },
      ],
      submitLoading: false,
    }
  },
  watch: {
    '$i18n.locale': function (val) {
      this.locale = val.split('-')[0]
    },
    'form.code': function (val) {
      if (val) {
        this.form.code = val.toUpperCase().replace(/\s+/g, '_')
      }
    },
  },
  mounted() {
    this.fetchCategories()
  },
  methods: {
    formatErrorMessage(message) {
      return message ? this.$t(message) : ''
    },
    getNameByLang(data) {
      if (!data.name) return ''

      if (this.$i18n.locale === 'vi') {
        return data.name.vi ? data.name.vi : data.name.en
      }
      if (this.$i18n.locale === 'km') {
        return data.name.km ? data.name.km : data.name.en
      }
      if (this.$i18n.locale === 'en') {
        return data.name.en ? data.name.en : data.name.zh
      }
      return data.name.zh ? data.name.zh : data.name.en
    },
    async fetchCategories() {
      try {
        this.loading = true
        const response = await ServiceSystemRepository.getServiceCategory({
          ...this.params,
          perPage: this.perPage,
        })
        this.categories = response.data.data.list || response.data.data
        this.total = response.data.data.total || this.categories.length
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    openModal(category = null) {
      this.modalMode = category ? 'edit' : 'add'
      if (category) {
        this.selectedCategory = category
        this.form.name = {
          en: category.name?.en || '',
          zh: category.name?.zh || '',
          km: category.name?.km || '',
          vi: category.name?.vi || '',
        }
        this.form.code = category.code || ''
        this.form.description = category.description || ''
        this.form.isActive = category.isActive ?? true
      }
      this.showModal = true
    },
    async handleSubmit() {
      this.submitLoading = true
      const isValid = await this.$refs.createForm.validate()
      if (!isValid) return

      try {
        const payload = {
          name: this.form.name,
          code: this.form.code.trim().toUpperCase().replace(/\s+/g, '_'),
          description: this.form.description,
        }

        if (this.modalMode === 'edit') {
          payload.isActive = this.form.isActive
        }

        if (this.modalMode === 'add') {
          await ServiceSystemRepository.createCategory(payload)
        } else {
          await ServiceSystemRepository.updateCategory(this.selectedCategory.id, payload)
        }
        this.showModal = false
        this.fetchCategories()
      } catch (error) {
        console.error(error)
      } finally {
        this.submitLoading = false
      }
    },
    async handleDelete(id) {
      try {
        await ServiceSystemRepository.deleteCategory(id)
        this.fetchCategories()
      } catch (error) {
        console.error(error)
      }
    },
    resetForm() {
      this.form = {
        name: {
          en: '',
          zh: '',
          km: '',
          vi: '',
        },
        code: '',
        description: '',
        isActive: true,
      }
      this.selectedCategory = null
    },
    onPageChange(page) {
      this.params.page = page
      this.fetchCategories()
    },
    search() {
      this.params.page = 1
      this.fetchCategories()
    },
    reset() {
      this.$refs.search.reset()
      this.params = {
        page: 1,
        name: null,
        order: null,
        sort: null,
      }
      this.fetchCategories()
    },
    updateQueryString() {
      this.$router.replace({ query: this.params })
    },
    sortChanged(sortBy, sortDirection) {
      this.params.order = sortBy
      this.params.sort = sortDirection
      this.updateQueryString()
      this.fetchCategories()
    },
    confirmDelete(id) {
      this.$bvModal
        .msgBoxConfirm(
          this.$t('alert.unableToRevertAfterDelete', {
            item: this.getNameByLang(this.categories.find(item => item.id === id)),
          }),
          {
            title: `${this.$t('button.confirm')}!`,
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'primary',
            okTitle: this.$t('button.delete'),
            cancelTitle: this.$t('button.cancel'),
            cancelVariant: 'outline-secondary',
            hideHeaderClose: false,
            centered: true,
          },
        )
        .then(value => {
          if (value) {
            this.handleDelete(id)
          }
        })
    },
  },
  setup() {
    // const permissionType = 'service-category'
    const permissionType = 'manage-users'
    const route = 'service-category'
    const manageRemotePermission = 'manage-remote-service-category'

    return {
      permissionType,
      route,
      manageRemotePermission,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.per-page-selector {
  width: 90px;
}
</style>
